import {Link} from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { membersApi, membersError, Member, MemberReferral, memberDate, memberStatus } from '../../services/membersApi.ts';
import { Card, Field, Loading, Notice, Pagination, primary } from './MembersShared.tsx';
import { useTheme } from '../../contexts/ThemeContext.tsx';
export default function MembersAdmin() {
  const {
    isDark
  } = useTheme();
  const [partners, setPartners] = useState<Member[]>([]);
  const [referrals, setReferrals] = useState<MemberReferral[]>([]);
  const [tab, setTab] = useState<'partners' | 'referrals'>('partners');
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<MemberReferral | null>(null);
  const [provisionPartner, setProvisionPartner] = useState<Member | null>(null);
  const refresh = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      if (tab === 'partners') {
        const response = await membersApi.partners(offset);
        setPartners(response.items);
        setTotal(response.total);
      } else {
        const response = await membersApi.referrals(offset);
        setReferrals(response.items);
        setTotal(response.total);
      }
    } catch (err) {
      setError(membersError(err));
    } finally {
      setBusy(false);
    }
  }, [tab, offset]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  async function assign(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selected) return;
    const values = new FormData(event.currentTarget);
    setBusy(true);
    setError('');
    try {
      await membersApi.assign(selected.id, {
        clinic_id: Number(values.get('clinic_id')),
        contract_start: String(values.get('contract_start'))
      });
      setSelected(null);
      await refresh();
    } catch (err) {
      setError(membersError(err));
    } finally {
      setBusy(false);
    }
  }
  async function financialAction(partner: Member, provision = false) {
    setBusy(true);
    setError('');
    try {
      if (provision) await membersApi.provision(partner.id);else await membersApi.sync(partner.id);
      setProvisionPartner(null);
      await refresh();
    } catch (err) {
      setError(membersError(err));
      setProvisionPartner(null);
    } finally {
      setBusy(false);
    }
  }
  return <div className={`min-h-screen p-4 sm:p-8 members-theme ${isDark ? 'members-theme-dark' : ''}`}>
    <div className="mx-auto max-w-6xl space-y-6">
      <h1 className="text-2xl font-semibold">Programa de parceiros</h1>
      <Link to="/members/admin/email-history" className="members-link inline-flex min-h-[40px] items-center text-sm underline">Histórico de e-mails</Link>
      <Link to="/members/admin/directory" className="members-link inline-flex min-h-[40px] items-center text-sm underline">Revisar catálogo público</Link>
      <p className="text-sm opacity-75">Acompanhe agências, profissionais de marketing e clientes Sminy no programa. Vincule as indicações às clínicas contratadas.</p>
      <nav className="flex gap-3" aria-label="Administração de parceiros">{[['partners', 'Parceiros'], ['referrals', 'Indicações']].map(([key, label]) => <button key={key} className={tab === key ? primary : 'rounded-xl border px-5 py-3 text-sm'} onClick={() => {
          setTab(key as typeof tab);
          setOffset(0);
        }}>{label}</button>)}</nav>{error && <Notice error>{error}</Notice>}{busy && <Loading />}{!busy && <Card>{tab === 'partners' ? partners.length ? <div className="members-table-frame"><table className="members-table members-partners-table"><caption className="sr-only">Parceiros cadastrados</caption><thead><tr><th scope="col">Parceiro</th><th scope="col">Financeiro</th><th scope="col">Ação</th></tr></thead><tbody>{partners.map(partner => <tr key={partner.id}><th scope="row"><span className="font-semibold">{partner.name}</span><span className="members-muted mt-2 block font-normal">{partner.email}</span><span className="members-muted block font-normal">{partner.phone}</span><span className="members-muted mt-2 block font-normal">Parceiro #{partner.id}</span></th><td>{memberStatus(partner.status)}</td><td>{partner.provisioning_status === 'not_started' ? <button onClick={() => setProvisionPartner(partner)} className={`${primary} members-table-action`}>Ativar cadastro financeiro</button> : partner.provisioning_status === 'created' ? <button onClick={() => void financialAction(partner)} className="members-table-action rounded-xl border px-3 py-2">Atualizar aprovação</button> : <p>Criação em conferência. Atendimento da Sminy necessário.</p>}</td></tr>)}</tbody></table></div> : <p className="py-10 text-center text-sm">Nenhum parceiro cadastrado.</p> : referrals.length ? <div className="divide-y divide-gray-400/20">{referrals.map(item => <article key={item.id} className="flex flex-col justify-between gap-3 py-4 sm:flex-row">
            <div>
              <h2 className="font-semibold">{item.clinic_name}</h2>
              <p className="text-sm opacity-75">{item.contact_name} · {item.email} · {item.phone}</p>
              <p className="text-sm opacity-75">{memberStatus(item.status)} · {memberDate(item.created_at)} · Parceiro #{item.partner_id}</p>
            </div>{!item.contract_start && <button onClick={() => setSelected(item)} className={primary}>Vincular contrato</button>}</article>)}</div> : <p className="py-10 text-center text-sm">Nenhuma indicação recebida.</p>}
        <Pagination offset={offset} total={total} onChange={setOffset} />
      </Card>}{selected && <Card>
        <h2 className="font-semibold">Vincular contrato: {selected.clinic_name}</h2>
        <p className="my-3 text-sm opacity-75">Confirme a identificação da clínica contratada antes de salvar. Este vínculo define a carteira do parceiro.</p>
        <form onSubmit={assign} className="space-y-4">
          <Field label="ID da clínica contratada" name="clinic_id" type="number" min="1" step="1" required />
          <Field label="Início do contrato" name="contract_start" type="date" required />
          <div className="flex flex-wrap gap-3">
            <button type="button" onClick={() => setSelected(null)} className="rounded-xl border px-4 py-3">Cancelar</button>
            <button disabled={busy} className={primary}>Confirmar vínculo</button>
          </div>
        </form>
      </Card>}{provisionPartner && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" aria-labelledby="members-provision-title" onKeyDown={event => {
        if (event.key === 'Escape' && !busy) setProvisionPartner(null);
          if (event.key === 'Tab') {
            const buttons = event.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)');
            const first = buttons[0]; const last = buttons[buttons.length - 1];
            if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last?.focus();}
            if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first?.focus();}
          }
      }}>
        <Card className="w-full max-w-lg">
          <h2 id="members-provision-title" className="text-lg font-semibold">Ativar cadastro financeiro</h2>
          <p className="my-4 text-sm">Esta ação enviará os dados de {provisionPartner.name} ao Asaas para criar a conta de recebimento. Confirme que o cadastro foi revisado.</p>
          <div className="flex flex-wrap justify-end gap-3">
            <button autoFocus disabled={busy} onClick={() => setProvisionPartner(null)} className="rounded-xl border px-4 py-3">Cancelar</button>
            <button disabled={busy} onClick={() => void financialAction(provisionPartner, true)} className={primary}>Confirmar envio ao Asaas</button>
          </div>
        </Card>
      </div>}</div>
  </div>;
}
